import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  ComposedChart,
  Area,
} from 'recharts';
import { COLORS } from '../constants';
import EmptyState from './EmptyState';
import { formatCurrency } from '../utils';

export default function PerformanceTab({ timeSeries }) {
  const monthlyChartData = useMemo(() => {
    if (
      !timeSeries?.monthlyClicks?.length &&
      !timeSeries?.monthlyConversions?.length
    )
      return [];
    const clicks = timeSeries.monthlyClicks || [];
    const conversions = timeSeries.monthlyConversions || [];
    const keys = new Set([
      ...clicks.map((c) => c.month),
      ...conversions.map((c) => c.month),
    ]);
    return Array.from(keys).map((month) => {
      const c = clicks.find((x) => x.month === month) || {};
      const v = conversions.find((x) => x.month === month) || {};
      return {
        month,
        clicks: c.clicks ?? 0,
        conversions: v.conversions ?? 0,
        revenue: v.revenue ?? 0,
      };
    });
  }, [timeSeries]);

  if (monthlyChartData.length === 0) {
    return (
      <EmptyState
        message="No time-series data yet"
        subMessage="Clicks and conversions will show here over time."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-slate-900">
          Clicks & conversions over time
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthlyChartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                stroke="#94a3b8"
              />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                formatter={(value, name) =>
                  [name === 'revenue' ? formatCurrency(value) : value, name]
                }
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="clicks"
                name="Clicks"
                fill={COLORS.primaryLight}
                stroke={COLORS.primary}
                strokeWidth={2}
                yAxisId="left"
              />
              <Bar
                dataKey="conversions"
                name="Conversions"
                fill={COLORS.violet}
                radius={[4, 4, 0, 0]}
                yAxisId="left"
              />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue ($)"
                stroke={COLORS.emerald}
                strokeWidth={2}
                dot={{ r: 4 }}
                yAxisId="right"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-slate-900">
            Monthly clicks
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={timeSeries?.monthlyClicks || []}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8 }} />
                <Line
                  type="monotone"
                  dataKey="clicks"
                  stroke={COLORS.primary}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Clicks"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-slate-900">
            Monthly conversions & revenue
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={timeSeries?.monthlyConversions || []}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: 8 }}
                  formatter={(v, name) =>
                    [name === 'revenue' ? formatCurrency(v) : v, name]
                  }
                />
                <Bar
                  dataKey="conversions"
                  fill={COLORS.violet}
                  radius={[4, 4, 0, 0]}
                  name="Conversions"
                />
                <Bar
                  dataKey="revenue"
                  fill={COLORS.emerald}
                  radius={[4, 4, 0, 0]}
                  name="Revenue ($)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
